import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableSortDirection,
  DashboardTableMultiSelectFilter,
  DashboardTableSearchInput,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useDemandPartnersQuery, useDemandPartnerStatusCountsQuery } from '../../hooks';
import {
  demandPartnerPageSizes,
  DemandPartnerSortDirection,
  DemandPartnerStatus,
  isDemandPartnerSortProperty,
  type DemandPartner,
} from '../../model';
import { useDemandPartnersRouteFilters } from '../../useRouteFilters';
import { DemandPartnerAdsTxtDialog } from '../DemandPartnerAdsTxtDialog/DemandPartnerAdsTxtDialog';
import { createDemandPartnersTableColumns } from './columns';

export function DemandPartnersTable() {
  const navigate = useNavigate();
  const { can } = useShigoPermissions();
  const routeFilters = useDemandPartnersRouteFilters();
  const { filter } = routeFilters;
  const query = useDemandPartnersQuery(filter);
  const counts = useDemandPartnerStatusCountsQuery(filter.searchTerm);
  const search = useDebouncedValue(filter.searchTerm, routeFilters.setSearch, 650);
  const [adsTxtPartner, setAdsTxtPartner] = useState<DemandPartner | null>(null);
  const canCreate = can(ShigoPermission.DemandPartnersCreate);
  const canEdit = can(ShigoPermission.DemandPartnersEdit);
  const columns = useMemo(
    () =>
      createDemandPartnersTableColumns({
        canEdit,
        navigate,
        onShowAdsTxt: setAdsTxtPartner,
      }),
    [canEdit, navigate],
  );

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 md:py-6">
      <DashboardDataTable
        title="Demand Partners"
        tableLabel="Demand Partners"
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(partner) => partner.id}
        loading={query.isLoading}
        loadingMessage="Loading Demand Partners"
        errorMessage={query.isError ? 'Demand Partners could not be loaded.' : undefined}
        emptyMessage="No Demand Partners found."
        minWidthClassName="min-w-[1480px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label="Search Demand Partners"
              placeholder="Search Demand Partners"
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            <DashboardTableMultiSelectFilter
              ariaLabel="Filter Demand Partners by status"
              label={
                filter.statuses.length
                  ? filter.statuses.map(statusLabel).join(', ')
                  : 'All statuses'
              }
              value={filter.statuses}
              options={Object.values(DemandPartnerStatus).map((status) => ({
                value: status,
                label: statusLabel(status),
                count: counts.data?.[status] ?? 0,
              }))}
              onChange={routeFilters.setStatuses}
            />
            {canCreate ? (
              <Button asChild>
                <Link to="/demand/partners/create">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  New Demand Partner
                </Link>
              </Button>
            ) : null}
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: demandPartnerPageSizes,
          disabled: query.isFetching,
          onChange: (value) => {
            routeFilters.setPagination(value.page, value.pageSize);
          },
        }}
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === DemandPartnerSortDirection.Asc
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (value) => {
            if (!value) {
              routeFilters.setSort(null, filter.sortDirection);
              return;
            }
            if (!isDemandPartnerSortProperty(value.key)) return;
            routeFilters.setSort(
              value.key,
              value.direction === DashboardDataTableSortDirection.Asc
                ? DemandPartnerSortDirection.Asc
                : DemandPartnerSortDirection.Desc,
            );
          },
        }}
      />
      {adsTxtPartner ? (
        <DemandPartnerAdsTxtDialog
          partner={adsTxtPartner}
          onOpenChange={(open) => {
            if (!open) setAdsTxtPartner(null);
          }}
        />
      ) : null}
    </main>
  );
}

function statusLabel(status: DemandPartnerStatus) {
  return status.charAt(0) + status.slice(1).toLowerCase();
}
